import { createContext, useContext, useCallback, useState } from "react";
import confetti from "canvas-confetti";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Heart, Sparkles } from "lucide-react";

const Ctx = createContext(null);
const BRAND = ["#0B4A3F", "#0E6E5C", "#C08A2D", "#E7F3EE", "#f472b6"];

function burst() {
  const end = Date.now() + 1000;
  (function frame() {
    confetti({ particleCount: 5, angle: 60, spread: 60, startVelocity: 55, origin: { x: 0, y: 0.7 }, colors: BRAND });
    confetti({ particleCount: 5, angle: 120, spread: 60, startVelocity: 55, origin: { x: 1, y: 0.7 }, colors: BRAND });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
  confetti({ particleCount: 140, spread: 100, startVelocity: 45, origin: { y: 0.35 }, colors: BRAND, scalar: 1.1 });
}

export function MatchCelebrationProvider({ children }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);

  const celebrate = useCallback((p) => { setProfile(p || {}); setTimeout(burst, 120); }, []);
  const close = () => setProfile(null);

  const meInitials = (user?.profile?.name || "U").split(" ").map((n) => n[0]).slice(0, 2).join("");
  const themInitials = (profile?.name || profile?.firstName || "?").split(" ").map((n) => n[0]).slice(0, 2).join("");

  return (
    <Ctx.Provider value={{ celebrate }}>
      {children}
      <Dialog open={!!profile} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-sm overflow-hidden border-0 bg-gradient-to-b from-emerald-700 via-emerald-800 to-emerald-950 p-0 text-center text-white shadow-2xl" data-testid="match-celebration">
          <DialogTitle className="sr-only">It's a match</DialogTitle>
          <div className="px-6 pb-8 pt-10">
            <div className="mb-5 flex items-center justify-center">
              <Avatar className="h-20 w-20 border-4 border-white shadow-lg">
                {user?.profile?.photo && <AvatarImage src={user.profile.photo} alt="" className="object-cover" />}
                <AvatarFallback className="bg-emerald-900 text-lg text-amber-200">{meInitials}</AvatarFallback>
              </Avatar>
              <span className="z-10 -mx-4 flex h-12 w-12 items-center justify-center rounded-full bg-rose-500 shadow-lg ring-4 ring-emerald-800">
                <Heart className="h-6 w-6 fill-white text-white" />
              </span>
              <Avatar className="h-20 w-20 border-4 border-white shadow-lg">
                {profile?.photo && <AvatarImage src={profile.photo} alt="" className="object-cover" />}
                <AvatarFallback className="bg-emerald-900 text-lg text-amber-200">{themInitials}</AvatarFallback>
              </Avatar>
            </div>
            <p className="flex items-center justify-center gap-1.5 text-sm font-semibold uppercase tracking-[0.25em] text-amber-300"><Sparkles className="h-4 w-4" /> Mashallah</p>
            <h2 className="mt-1 font-serif text-4xl font-bold">It's a Match!</h2>
            <p className="mt-2 text-emerald-100/90">You and <b>{profile?.firstName || profile?.name || "this member"}</b> both expressed interest. May Allah bless your journey.</p>
            <div className="mt-7 space-y-2.5">
              <button onClick={() => { close(); navigate("/matches"); }} data-testid="celebration-view-matches" className="w-full rounded-full bg-amber-500 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-amber-600">View in My Matches</button>
              <button onClick={close} data-testid="celebration-keep-exploring" className="w-full rounded-full border border-white/30 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10">Keep exploring</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Ctx.Provider>
  );
}

export const useCelebrate = () => useContext(Ctx)?.celebrate || (() => {});
